import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import PlusIcon from "../../images/PlusIcon";
import CloseIcon from "../../images/CloseIcon";
import { deleteLocations, getLocations } from "../../services/locations.services";
import { useResponsive } from "../../hook/useResponsive";
import LocationsIcon from "../../images/LocationsIcon";
import { showApiError } from "../../services/apiError";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import TagStatus from "../../components/TagStatus";
import { ConfirmSheet } from "../../components/mobile";
import {
    ListColumn,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
    t,
} from "../../components/list";
interface LocationRow {
    id: string;
    locationCode: string;
    name: string;
    country: { countryName: string } | null;
    address: string;
    manager: string;
    phoneNumber: string;
    isActive: boolean;
}
interface Country {
    id: string;
    code: string;
    name: string;
}
interface InforTable {
    title: string;
    path: string;
    desc: string;
    titleCreate: string;
    img?: any;
}
interface LocationsResponse {
    data: {
        data: LocationRow[];
        count: number
    }

}
const Locations = () => {
    const preLanguage = localStorage.getItem("language");
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [language, setLanguage] = useState<any>(null);
    const userId: string = localStorage.getItem("userId") || "";
    const companyId: string = localStorage.getItem("companyId") || "";
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>("")
    const { isMobile, isTouchLayout } = useResponsive();
    const inforTable: InforTable = {
        title: language && language?.["No locations yet"] ? language?.["No locations yet"] : "No locations yet", path: "/locations/new", desc: language && language?.["Set up and oversee Sales Points such as Cafés, Pubs, or Rooftops to facilitate tailored menus, pricing strategies, and financial reports based on each location."] ? language?.["Set up and oversee Sales Points such as Cafés, Pubs, or Rooftops to facilitate tailored menus, pricing strategies, and financial reports based on each location."] : "Set up and oversee Sales Points such as Cafés, Pubs, or Rooftops to facilitate tailored menus, pricing strategies, and financial reports based on each location.", titleCreate: language && language?.["New Location"] ? language?.["New Location"] : "New Location",
        img: <LocationsIcon />
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    /** One page of locations; `searchValue` matches code, name, address, manager, phone and country. */
    const fetchLocations = async (query: ListQuery) => {
        webLoadingFalse();
        const response: LocationsResponse = await getLocations(userId, companyId, query.page, query.limit, query.search.trim());
        return { rows: response?.data?.data ?? [], count: response?.data?.count ?? 0 };
    };
    const goToLocation = (id: string) => webNavigate('/' + preLanguage + '/locations/' + id);
    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id)
    }
    const handleDeleteLocation = async () => {
        try {
            const response = await deleteLocations(idDelete);
        } catch (error) {
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false)
        }
    }

    /* ---------------- List kit configuration ---------------- */

    const columns: ListColumn<LocationRow>[] = [
        { key: 'locationCode', label: 'Code', width: 110, value: l => l.locationCode ?? '', mobile: 'subtitle' },
        {
            key: 'name',
            label: 'Name',
            width: 220,
            hideable: false,
            render: cells.primary(l => l.name),
            mobile: 'title',
            onClick: l => goToLocation(String(l.id)),
        },
        // The `city` FK points at the country table; the form labels it Country.
        { key: 'country', label: 'Country', width: 140, value: l => l.country?.countryName ?? '', mobile: 'subtitle' },
        { key: 'address', label: 'Address', width: 220, value: l => l.address ?? '', mobile: 'sheet' },
        { key: 'manager', label: 'Manager', width: 160, value: l => l.manager ?? '', mobile: 'sheet' },
        { key: 'phoneNumber', label: 'Phone Number', width: 150, value: l => l.phoneNumber ?? '', mobile: 'sheet' },
        {
            key: 'isActive',
            label: 'Status',
            width: 120,
            value: l => (l.isActive ? 'Active' : 'Not Active'),
            render: (l, ctx) => {
                const name = l.isActive ? 'Active' : 'Not Active';
                return <TagStatus value={name} label={t(ctx.language, name, name)} />;
            },
            mobile: 'trailing',
        },
    ];

    const rowActions: ListRowAction<LocationRow>[] = [
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: l => goToLocation(String(l.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: l => handleShowDelete(String(l.id)),
        },
    ];
    return (
        <>
            {/* Desktop is a flex column so the list fills the height left under the title row. */}
            <div
                className="h-full relative"
                style={{
                    height: '100%',
                    overflowY: isTouchLayout ? "auto" : undefined,
                    paddingBottom: isTouchLayout ? "24px" : undefined,
                    display: isTouchLayout ? undefined : 'flex',
                    flexDirection: isTouchLayout ? undefined : 'column',
                }}
            >
                <div className="">
                    <div className="lg:flex items-center justify-between">
                        <p style={{ color: "#222525", fontSize: '24px', }} className="font-semibold font-inter">{language && language?.["All locations"] ? language?.["All locations"] : "All locations"}</p>
                        <div className="cursor-pointer flex items-center justify-center" style={{ width: "fit-content", marginTop: isMobile ? "20px" : '0px', gap: '8px', padding: "8px 24px 8px 16px", backgroundColor: '#0F7679', borderRadius: '30px' }} onClick={() => { webNavigate("/" + preLanguage + '/locations/new') }}>
                            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                                <PlusIcon />
                            </div>
                            <p style={{ color: "#FFFFFF" }} className="text-[14px] leading-[130%] font-medium font-inter" >{language && language?.["Add location"] ? language?.["Add location"] : "Add location"}</p>
                        </div>
                    </div>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchLocations}
                    searchPlaceholder="Search locations"
                    searchInputRef={searchInputRef}
                    rowActions={rowActions}
                    countNoun="locations"
                    emptyState={inforTable}
                />
            </div>
            {isTouchLayout ? (
                <ConfirmSheet
                    open={showDelete}
                    title={language && language?.["Delete location?"] ? language?.["Delete location?"] : "Delete location?"}
                    message={language && language?.["This location will be permanently deleted. Are you sure you want to continue"] ? language?.["This location will be permanently deleted. Are you sure you want to continue"] : "This location will be permanently deleted. Are you sure you want to continue"}
                    confirmLabel={language && language?.["Delete"] ? language?.["Delete"] : "Delete"}
                    cancelLabel={language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
                    onConfirm={handleDeleteLocation}
                    onCancel={() => setShowDelete(false)}
                />
            ) : (
                <div
                    className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
                    style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
                >
                    <div
                        className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", transition: "transform 0.3s ease, opacity 0.3s ease",
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <p style={{ fontSize: '20px', color: '#222525' }} className="font-semibold font-inter leading-[24px]">
                                {language && language?.["Delete location?"] ? language?.["Delete location?"] : "Delete location?"}
                            </p>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="flex items-center justify-center cursor-pointer"
                                style={{
                                    width: "24px",
                                    height: "24px",
                                }}
                            >
                                <CloseIcon />
                            </div>
                        </div>
                        <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
                                {language && language?.["This location will be permanently deleted. Are you sure you want to continue"] ? language?.["This location will be permanently deleted. Are you sure you want to continue"] : "This location will be permanently deleted. Are you sure you want to continue"}
                            </p>
                            {/* <span className="text-main">[sale ID]</span> sale. */}
                        </div>
                        <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: "40px",
                                    padding: "10px 24px",
                                    border: "1px solid #C9D7D8",
                                    color: "#222525",
                                }}
                            >
                                {language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
                            </div>
                            <div
                                onClick={handleDeleteLocation}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: "40px",
                                    padding: "10px 24px",
                                    backgroundColor: "#BA0E14",
                                    color: "#FFFFFF",
                                }}
                            >
                                {language && language?.["Delete"] ? language?.["Delete"] : "Delete"}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
};

export default Locations;